/* Forged Racing — cart drawer. /cart.js endpoints, discount-at-checkout
   workaround, complementary upsells, global window.ForgedCart API. */
(function () {
  'use strict';

  var DISCOUNT_KEY = 'forged-discount-code';
  var root = null;
  var refs = {};
  var lastCart = null;

  function money(cents) {
    var symbol = (document.body.dataset.currency === 'USD' || !document.body.dataset.currency) ? '$' : document.body.dataset.currency + ' ';
    return symbol + (cents / 100).toFixed(2);
  }

  function esc(text) {
    var div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  // cart/recommendation endpoints return full-size CDN urls
  function sized(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function getDiscount() {
    try {
      return sessionStorage.getItem(DISCOUNT_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function setDiscount(code) {
    try {
      if (code) {
        sessionStorage.setItem(DISCOUNT_KEY, code);
      } else {
        sessionStorage.removeItem(DISCOUNT_KEY);
      }
    } catch (e) {
      /* noop */
    }
    syncDiscountUi();
  }

  function syncDiscountUi() {
    var code = getDiscount();
    var form = refs.footer;
    if (code) {
      form.setAttribute('action', '/checkout?discount=' + encodeURIComponent(code));
      refs.discountLabel.textContent = code;
      refs.discountApplied.hidden = false;
      refs.discountPanel.hidden = true;
      refs.discountToggle.setAttribute('aria-expanded', 'false');
    } else {
      form.setAttribute('action', '/cart');
      refs.discountApplied.hidden = true;
    }
  }

  function updateHeaderBadge(count) {
    document.querySelectorAll('.forged-header__cart-badge').forEach(function (badge) {
      badge.textContent = count > 99 ? '99+' : count;
      badge.style.display = count > 0 ? '' : 'none';
    });
  }

  /* ==================================================================
     ROUND 70 — which cart lines are backordered.

     The set comes from Liquid (snippets/forged-config.liquid) because nothing
     client-side can answer this: /cart.js carries no inventory fields, and
     /products/<handle>.js exposes inventory_management but NOT
     inventory_quantity or inventory_policy — checked against the live store
     rather than assumed. `available` is true for a +504 and a -9 variant alike,
     so it cannot discriminate either.

     markBackordered() lets the PDP add its own variant to the set the moment it
     is added by AJAX, since the page-render list cannot know about it yet. That
     covers the route a backordered item is actually bought through.

     Everything here fails toward saying NOTHING. An id we have not been told
     about produces no label — a gap that the next page load closes — rather
     than a confident claim about an item we cannot see.
     ================================================================== */
  /* ROUND 88 — these lines are PRE-ORDERS (snippets/forged-variant-state) and
     are labelled with the plain word "Pre-order". The R70 ship window
     ("2–3 weeks", a setting default nobody chose) is gone: no ship dates or
     windows anywhere. */
  var BO_CFG = (window.ForgedConfig && window.ForgedConfig.backorder) || {};
  var BO_IDS = {};
  (BO_CFG.ids || []).forEach(function (id) { BO_IDS[String(id)] = true; });

  function isBackorderedLine(item) {
    return !!BO_IDS[String(item.variant_id)];
  }

  function markBackordered(variantId, on) {
    if (!variantId) return;
    if (on) { BO_IDS[String(variantId)] = true; }
    else { delete BO_IDS[String(variantId)]; }
  }

  /* ==================================================================
     ROUND 71 — split checkout for mixed carts.

     A cart holding a $4 sticker and a backordered brake kit currently ships
     nothing for three weeks. This lets the in-stock half go now and keeps the
     pre-order recoverable afterwards.

     DETECTION IS R70's, NOT A SECOND COPY. isBackorderedLine() above is the one
     place that answers "is this line backordered", fed by the id list Liquid
     emits in forged-config.liquid. Re-deriving it here would drift the moment
     one of them changed.
     ================================================================== */
  var PREORDER_KEY = 'frg:preorder-saved';

  function readSaved() {
    try {
      var raw = localStorage.getItem(PREORDER_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return (v && v.lines && v.lines.length) ? v : null;
    } catch (e) { return null; }
  }

  function writeSaved(lines) {
    try {
      localStorage.setItem(PREORDER_KEY, JSON.stringify({ savedAt: Date.now(), lines: lines }));
    } catch (e) { /* private mode: the split still works, recovery just will not persist */ }
  }

  function clearSaved() {
    try { localStorage.removeItem(PREORDER_KEY); } catch (e) { /* noop */ }
  }

  function splitBuckets(cart) {
    var back = [], instock = [];
    (cart.items || []).forEach(function (i) {
      (isBackorderedLine(i) ? back : instock).push(i);
    });
    return { back: back, instock: instock };
  }

  /* The notice renders ONLY for a genuinely mixed cart. All-in-stock has
     nothing to explain; all-backordered has nothing to split off, and offering
     "ship in-stock items now" on a cart with none would empty it. */
  /* Survives the re-render that follows a successful split. renderSplit() runs
     again as soon as refresh() lands, and by then the cart is no longer mixed —
     so without this the confirmation was written into an element that had
     already been cleared and hidden, and the customer saw nothing at all. Found
     in verification: the split worked and said so to no one. */
  var pendingSplitMsg = '';

  function renderSplit(cart) {
    if (!refs.split) return;
    var b = splitBuckets(cart);
    if (!b.back.length || !b.instock.length) {
      if (pendingSplitMsg) {
        refs.split.innerHTML = '<p class="frg-split__msg is-done" data-frg-split-msg>' +
          esc(pendingSplitMsg) + '</p>';
        refs.split.hidden = false;
        return;
      }
      refs.split.innerHTML = '';
      refs.split.hidden = true;
      return;
    }
    refs.split.innerHTML =
      '<p class="frg-split__line">Your order includes a pre-order item, ' +
      'so everything would ship together.</p>' +
      '<button type="button" class="frg-split__action" data-frg-split-go>' +
      'Ship in-stock items now instead</button>' +
      '<p class="frg-split__msg" data-frg-split-msg role="status" hidden></p>';
    refs.split.hidden = false;
  }

  function splitMsg(text, isError) {
    var el = refs.split && refs.split.querySelector('[data-frg-split-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', !!isError);
    el.hidden = !text;
  }

  /* ==================================================================
     ROUND 72 — make an abandoned pre-order visible in admin.

     R71's recovery lives in localStorage, which dies with the tab. If the
     customer closes the thank-you page and never comes back, the pre-order is
     gone and NOTHING anywhere records that it existed — not in admin, not on
     the order, nowhere to follow up from.

     Cart attributes fix exactly that: they survive checkout and land on the
     order record server-side, so an abandoned pre-order becomes a row someone
     can act on instead of silence.

     THE LEADING UNDERSCORE IS LOAD-BEARING. Shopify hides attributes whose key
     starts with "_" from the order status page and the customer confirmation
     email while still storing them on the order. Without it the buyer would
     read "_forged_split: pending" on their receipt, which is both confusing
     and looks broken.

     LENGTH. Shopify caps attribute values, and a cart can hold several
     backordered lines with long product titles. The summary is truncated to a
     safe ceiling with an ellipsis and the count appended, so the value stays
     valid and still says how many items were involved rather than silently
     losing the tail.
     ================================================================== */
  var SPLIT_ATTR_FLAG = '_forged_split';
  var SPLIT_ATTR_ITEMS = '_forged_preorder_items';
  var SPLIT_ATTR_MAX = 240;   /* comfortably inside Shopify's limit */

  function preorderSummary(saved) {
    var parts = saved.map(function (l) {
      return String(l.title || 'item') +
             (l.variant ? ' (' + l.variant + ')' : '') +
             ' x' + (l.quantity || 1);
    });
    var full = parts.join('; ');
    if (full.length <= SPLIT_ATTR_MAX) return full;
    /* Truncate on the joined string, then say how many lines it covered so the
       tail is accounted for rather than just missing. */
    var suffix = '… (' + parts.length + ' items)';
    return full.slice(0, Math.max(0, SPLIT_ATTR_MAX - suffix.length)) + suffix;
  }

  /* Resolves either way. A failed attribute write must NOT block the split:
     the customer asked to ship their in-stock items and that intent wins. A
     split with no attribute is exactly today's behaviour, which is acceptable;
     a removed line with no record is what this round exists to prevent, and
     that only happens if the split itself fails. */
  function writeSplitAttributes(saved) {
    var attrs = {};
    attrs[SPLIT_ATTR_FLAG] = 'pending';
    attrs[SPLIT_ATTR_ITEMS] = preorderSummary(saved);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('attribute write failed: ' + r.status);
        return true;
      })
      .catch(function (e) {
        if (window.console && console.warn) {
          console.warn('FORGED_SPLIT_ATTR_FAILED', e && e.message);
        }
        return false;
      });
  }

  /* Clearing sets the keys to empty strings rather than omitting them —
     /cart/update.js only removes an attribute when it is explicitly set to "".
     Omitting it leaves the previous value in place, which is the failure mode
     that would tag a completed pre-order as still pending. */
  function clearSplitAttributes() {
    var attrs = {};
    attrs[SPLIT_ATTR_FLAG] = '';
    attrs[SPLIT_ATTR_ITEMS] = '';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () { /* best effort; a fresh cart carries nothing anyway */ });
  }

  /* ROUND 71 — the split itself.

     ORDER OF OPERATIONS IS DELIBERATE. The lines are saved to localStorage
     BEFORE anything is removed from the cart. If the removal then fails, the
     customer still has both: the cart is untouched and the saved set is
     harmlessly redundant, because renderRecover() only offers a restore when a
     saved set exists AND those variants are not already in the cart.

     Removal uses /cart/change.js by LINE, one at a time, highest line first.
     Line numbers shift down as lines are removed, so descending order keeps
     every subsequent index valid — ascending would silently remove the wrong
     rows. change.js is also the endpoint that actually enforces quantity, per
     the Round 37 correction.

     Any failure aborts the remaining removals and re-renders from the server's
     real cart, so the drawer can never show a half-applied state. */
  function doSplit() {
    var cart = lastCart;
    if (!cart) return;
    var b = splitBuckets(cart);
    if (!b.back.length || !b.instock.length) return;

    var saved = b.back.map(function (i) {
      /* ROUND 72 adds `variant`. The summary has to name WHICH variant: this
         product carries six and only two are backordered, so "Brake Upgrade
         Kit x1" tells whoever follows up nothing they can act on. Additive, so
         a payload saved by R71 before this shipped still reads fine. */
      return {
        id: i.variant_id,
        quantity: i.quantity,
        title: i.product_title,
        variant: (i.variant_title && i.variant_title !== 'Default Title') ? i.variant_title : ''
      };
    });
    writeSaved(saved);

    /* Descending line numbers — see above. */
    var lines = b.back
      .map(function (i) { return cart.items.indexOf(i) + 1; })
      .sort(function (a, c) { return c - a; });

    var btn = refs.split && refs.split.querySelector('[data-frg-split-go]');
    if (btn) { btn.disabled = true; }
    splitMsg('');

    /* ROUND 72 — attributes are written FIRST and the chain waits on them.

       Order matters: once the lines are removed, the cart no longer knows what
       they were, so an attribute write that ran afterwards would be describing
       something it can no longer see if anything went wrong in between. Writing
       first means the record exists before the thing it records is destroyed.

       writeSplitAttributes resolves on failure too, so a rejected write logs
       and the split still proceeds. */
    var chain = writeSplitAttributes(saved);
    var failed = false;
    lines.forEach(function (line) {
      chain = chain.then(function () {
        if (failed) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: line, quantity: 0 })
        }).then(function (r) {
          if (!r.ok) { failed = true; throw new Error('change failed'); }
          return r.json();
        });
      });
    });

    return chain
      .then(function () {
        /* Set BEFORE refresh: the re-render is what paints it. */
        pendingSplitMsg = 'Pre-order saved — you can complete it right after checkout.';
        return refresh();
      })
      .then(function () { if (btn) { btn.disabled = false; } })
      .catch(function () {
        if (btn) { btn.disabled = false; }
        pendingSplitMsg = '';
        /* Re-read the server's cart so the drawer reflects reality, not the
           optimistic state we were mid-way through. */
        return refresh().then(function () {
          splitMsg('Could not update the cart. Nothing was changed — please try again.', true);
        });
      });
  }

  /* ROUND 71 — recovery.

     Offered whenever a saved pre-order exists and its variants are NOT already
     in the cart. That second test is what makes re-adding safe: if the customer
     puts the backordered item back themselves, the saved set is cleared rather
     than offering to add a duplicate. */
  function savedNotInCart(cart) {
    var saved = readSaved();
    if (!saved) return null;
    var inCart = {};
    (cart && cart.items ? cart.items : []).forEach(function (i) { inCart[String(i.variant_id)] = true; });
    var missing = saved.lines.filter(function (l) { return !inCart[String(l.id)]; });
    if (!missing.length) {
      /* Every saved line is back in the cart on its own — the split has been
         undone by hand, so the saved set is stale. */
      clearSaved();
      return null;
    }
    return { savedAt: saved.savedAt, lines: missing };
  }

  function recoverMarkup(saved, compact) {
    var names = saved.lines.map(function (l) { return esc(l.title || 'your pre-order'); });
    var joined = names.length > 1
      ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
      : names[0];
    return (
      '<p class="frg-recover__title">Your pre-order is ready to complete</p>' +
      '<p class="frg-recover__items">' + joined + '</p>' +
      '<button type="button" class="frg-recover__go" data-frg-recover-go>Complete pre-order →</button>' +
      (compact ? '' : '<p class="frg-recover__msg" data-frg-recover-msg role="status" hidden></p>')
    );
  }

  function renderRecover(cart) {
    var saved = savedNotInCart(cart);
    if (refs.recover) {
      if (saved) { refs.recover.innerHTML = recoverMarkup(saved); refs.recover.hidden = false; }
      else { refs.recover.innerHTML = ''; refs.recover.hidden = true; }
    }
    var bar = document.querySelector('[data-frg-preorder-bar]');
    if (bar) {
      if (saved) { bar.innerHTML = recoverMarkup(saved, true); bar.hidden = false; }
      else { bar.innerHTML = ''; bar.hidden = true; }
      /* ROUND 71 — lift the Inbox launcher clear of the bar.

         Hit-tested at 390: with the bar up, 2 of 6 probe points across
         "Complete pre-order" returned shopify-chat — the right ~30% of the one
         button that recovers the pre-order was a dead click. Exactly the defect
         Rounds 19 and 26 fixed for the checkout button.

         Uses Round 64's lift rather than Round 19's display:none. The launcher
         is only suppressed for a transient overlay there; this bar can persist
         for days until the pre-order is completed, and hiding live chat that
         long to protect one button is the wrong trade. Moving it up costs
         nothing and reverts with the class. */
      document.body.classList.toggle('frg-pobar-on', !!saved);
    }
  }

  /* Adds the saved lines back and goes straight to checkout.

     CLEARED ONLY AFTER the adds resolve, so a failed restore leaves the saved
     set intact and the customer can try again. Clearing first would lose the
     pre-order on exactly the request that failed. */
  function doRecover() {
    var saved = readSaved();
    if (!saved) return;
    var btn = document.querySelector('[data-frg-recover-go]');
    if (btn) { btn.disabled = true; }
    /* ROUND 72 — clear the pending flag before rebuilding the pre-order cart.

       The assumption would be that the post-checkout cart is fresh and carries
       nothing. That is USUALLY true — Shopify issues a new cart token after an
       order — but it is not guaranteed for every path into this function: a
       customer who splits and then taps "Complete pre-order" WITHOUT checking
       out is still on the same cart, attributes and all. That path would carry
       _forged_split: pending straight onto the second order and make the admin
       view useless, which is the one outcome this round must not produce.

       Clearing unconditionally costs one request and removes the whole class of
       error, rather than reasoning about which paths are safe. */
    var chain = clearSplitAttributes();
    saved.lines.forEach(function (l) {
      chain = chain.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: l.id, quantity: l.quantity || 1 })
        }).then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); });
      });
    });
    return chain
      .then(function () {
        clearSaved();
        pendingSplitMsg = '';
        window.location.href = '/checkout';
      })
      .catch(function () {
        if (btn) { btn.disabled = false; }
        var msg = document.querySelector('[data-frg-recover-msg]');
        if (msg) {
          msg.textContent = 'Could not restore the pre-order. Your saved items are still here — please try again.';
          msg.hidden = false;
        }
      });
  }

  function renderItems(cart) {
    refs.items.innerHTML = cart.items
      .map(function (item, index) {
        var img = item.image || (item.featured_image && item.featured_image.url) || '';
        var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        return (
          '<li class="forged-cart__item" data-line="' + (index + 1) + '">' +
          '<span class="forged-cart__item-media">' +
          (img ? '<img src="' + esc(sized(img, 160)) + '" alt="" loading="lazy" width="80" height="80">' : '') +
          '</span>' +
          '<span class="forged-cart__item-info">' +
          '<a class="forged-cart__item-title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
          (variant ? '<span class="forged-cart__item-variant">' + esc(variant) + '</span>' : '') +
          /* ROUND 70 — backorder flag, directly under the title. This is the
             last screen before checkout and the last chance to set the
             expectation, because Shopify checkout cannot be modified on this
             plan. Subtle by instruction: small, secondary colour, no chrome. */
          (isBackorderedLine(item) ? '<span class="frg-bo frg-bo--cart">Pre-order</span>' : '') +
          '<span class="forged-cart__stepper">' +
          '<button type="button" data-forged-cart-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">−</button>' +
          '<span class="forged-cart__stepper-qty">' + item.quantity + '</span>' +
          '<button type="button" data-forged-cart-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
          '<button type="button" class="forged-cart__trash" data-forged-cart-qty="0" aria-label="Remove item">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m3 0l-.8 13a1 1 0 0 1-1 1H7.8a1 1 0 0 1-1-1L6 7"/></svg>' +
          '</button>' +
          '</span>' +
          '</span>' +
          '<span class="forged-cart__item-total">' + money(item.final_line_price) + '</span>' +
          '</li>'
        );
      })
      .join('');
  }

  /* ==================================================================
     ROUND 67 — cart upsell now comes from the `cart-upsell` TAG.

     WHAT CHANGED, so Emirali knows: the previous rule (Round 33/40) selected
     complementary recommendations under a $40 ceiling. That constraint was his
     and it was about PRICE — "make it more afordable items no like wheelsets" —
     not about popularity. It has been replaced, not tightened. The six tagged
     products were chosen from 90-day order data and include the Chrome Talaria
     Dirt Wheelset, which the old $40 rule would have excluded outright.

     Read from the tag rather than a handle list so products can be added or
     removed in admin without a code round.

     SOURCING. There is no tag-filtered products.json — /collections/all/<tag>/
     products.json returns 404, verified against the live store. The predictive
     search endpoint does index tags and returns exactly the tagged set for
     q=tag:cart-upsell, so that is the source. It does NOT return variant ids
     (its `variants` array comes back empty), so the variant for each product
     we actually intend to SHOW is fetched from /products/<handle>.js.

     COST — the drawer must not get slower. Both fetches are cached for the
     page's lifetime: the tag query resolves once, and each product's variant
     resolves once. Re-renders after add/remove reuse them and cost zero
     network. Only the ≤3 products that survive filtering are ever fetched, so
     a first open costs one small query plus at most three more.

     EXCLUSIONS are re-applied on EVERY render against the cart passed in, not
     cached with the product list, because the whole point is that an item
     disappears from the upsell the moment it enters the cart.
     ================================================================== */
  var UPSELL_TAG = 'cart-upsell';
  var UPSELL_MAX = 3;
  var taggedPromise = null;
  var variantCache = {};

  function fetchTagged() {
    if (taggedPromise) return taggedPromise;
    taggedPromise = fetch(
      '/search/suggest.json?q=' + encodeURIComponent('tag:' + UPSELL_TAG) +
      '&resources[type]=product&resources[limit]=10'
    )
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var res = d && d.resources && d.resources.results;
        return (res && res.products) || [];
      })
      .catch(function () { return []; });
    return taggedPromise;
  }

  function fetchProduct(handle) {
    if (variantCache[handle]) return variantCache[handle];
    variantCache[handle] = fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return variantCache[handle];
  }

  function upsellTile(p, variant) {
    var img = p.featured_image || p.image || '';
    if (img && typeof img === 'object') img = img.url || '';
    return (
      '<div class="forged-cart__upsell">' +
      '<a href="' + esc(p.url) + '" class="forged-cart__upsell-media">' +
      (img ? '<img src="' + esc(sized(img, 300)) + '" alt="" loading="lazy">' : '') +
      '</a>' +
      '<span class="forged-cart__upsell-name">' + esc(p.title) + '</span>' +
      '<span class="forged-cart__upsell-row">' +
      /* ROUND 50 rule kept: price the variant actually being added, never the
         product minimum, or the tile advertises one number and the button
         charges another. variant.price from /products/x.js is already cents. */
      '<span class="forged-cart__upsell-price">' + money(variant.price) + '</span>' +
      '<button type="button" class="forged-cart__upsell-add" data-forged-cart-add="' +
      variant.id + '">Add</button>' +
      '</span>' +
      '</div>'
    );
  }

  function renderUpsells(cart) {
    if (!cart.items.length) {
      refs.upsellGrid.innerHTML = '';
      refs.upsells.hidden = true;
      return;
    }
    var inCart = cart.items.map(function (i) { return i.product_id; });
    var giftIds = (window.ForgedGifts && window.ForgedGifts.giftVariantIds)
      ? window.ForgedGifts.giftVariantIds() : [];

    /* Empty the grid as well as hiding it. Verified with all six tagged
       products in the cart: the block hid correctly but three stale tiles
       stayed in the DOM. Nothing showed them today, because a later render
       rewrites innerHTML before unhiding — but leaving products that no longer
       qualify sitting in the markup is one async reordering away from
       displaying them. */
    function hide() {
      refs.upsellGrid.innerHTML = '';
      refs.upsells.hidden = true;
    }

    /* ROUND 75 — Emirali: "when u add stuff it just goes away, it doesn't keep
       recommending."

       REPRODUCED, then measured. The tag pool is 8 products and all 8 pass the
       stock filter, so it is NOT exhausting — the strip was starving itself:

         cart lines 1 -> 2 tiles        (should be 3)
         cart lines 2 -> 2 tiles
         cart lines 3 -> 1 tile
         cart lines 4 -> 1 tile

       CAUSE: the old code sliced to UPSELL_MAX *before* the remaining filters
       ran. It took the first 3 eligible-looking products, then dropped any that
       turned out to be a gift variant or to have no purchasable variant — and
       never went back to the pool to backfill. Every exclusion permanently cost
       a slot, so the strip shrank as the cart grew even with five untouched
       products sitting further down the list. That is why it looked like it
       "stopped recommending".

       FIX: filter FULLY first, then take three. Products are walked in order
       and their variants resolved until UPSELL_MAX qualify, so an excluded
       candidate is replaced rather than lost. Sequential and short-circuiting,
       so it fetches only as far down the pool as it needs — and fetchProduct is
       cached, so a re-render after an add costs no network at all. */
    fetchTagged()
      .then(function (products) {
        var pool = products.filter(function (p) {
          return p.available && inCart.indexOf(p.id) === -1;
        });
        if (!pool.length) { hide(); return null; }

        var usable = [];
        var chain = Promise.resolve();
        pool.forEach(function (p) {
          chain = chain.then(function () {
            if (usable.length >= UPSELL_MAX) return null;   /* already have enough */
            return fetchProduct(p.handle).then(function (full) {
              if (!full || !full.variants || !full.variants.length) return null;
              var v = null;
              for (var j = 0; j < full.variants.length; j++) {
                if (full.variants[j].available) { v = full.variants[j]; break; }
              }
              if (!v) return null;
              /* Never offer a gift variant: it is free elsewhere in this drawer. */
              if (giftIds.indexOf(v.id) !== -1) return null;
              usable.push({ p: p, variant: v });
              return null;
            }).catch(function () { return null; });   /* one bad product must not kill the strip */
          });
        });
        return chain.then(function () { return usable; });
      })
      .then(function (usable) {
        if (!usable) return;
        if (!usable.length) { hide(); return; }
        refs.upsellGrid.innerHTML = usable.map(function (u) {
          return upsellTile(u.p, u.variant);
        }).join('');
        refs.upsells.hidden = false;
      })
      .catch(hide);
  }

  function render(cart) {
    lastCart = cart;
    refs.count.textContent = cart.item_count;
    updateHeaderBadge(cart.item_count);
    var empty = cart.item_count === 0;
    refs.empty.hidden = !empty;
    refs.items.hidden = empty;
    refs.footer.hidden = empty;

    /* ROUND 40 — the free-gift system's only hook into this file.

       Handed the cart we already fetched rather than letting it fetch its own,
       and called on the empty branch too so the progress indicator clears when
       the cart empties instead of freezing on its last message.

       This theme has no cart:updated event, so render() is the single honest
       choke point for "the cart changed". forged-gifts.js guards re-entry with
       a mutex and a state fingerprint, so the resync it may trigger here
       settles after one pass rather than looping. */
    if (window.ForgedGifts && window.ForgedGifts.onCartRendered) {
      window.ForgedGifts.onCartRendered(cart);
    }

    /* ROUND 71 — evaluated on EVERY render, including the empty branch below,
       so splitting the last backordered line cannot leave a stale notice
       offering to split a cart that no longer has anything to split. */
    renderSplit(cart);
    renderRecover(cart);

    if (empty) {
      /* ROUND 67: clear the upsell GRID here, not just hide its wrapper. This
         branch returns before renderUpsells() ever runs, so the clear added
         inside that function is unreachable on the empty path — measured with
         an emptied cart, the wrapper hid correctly while three stale tiles
         stayed in the DOM. Same reasoning the gift hook above already applies:
         a cleared cart must not leave the last render frozen in place. */
      refs.upsellGrid.innerHTML = '';
      refs.upsells.hidden = true;
      refs.items.innerHTML = '';
      if (refs.split) { refs.split.innerHTML = ''; refs.split.hidden = true; }
      return;
    }
    renderItems(cart);
    refs.total.textContent = money(cart.total_price);
    syncDiscountUi();
    renderUpsells(cart);
  }

  function refresh() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () {
        /* ROUND 61. Shopify does not always answer /cart.js with JSON: a rate
           limit, a Cloudflare challenge, a maintenance page or a 5xx all come
           back as HTML, and .json() then rejects. Observed live while testing —
           the console filled with "Uncaught (in promise) SyntaxError:
           Unexpected token '<'".

           The rejection was the real damage, not the noise. refresh() is what
           open() waits on, so a failed fetch meant open()'s .then() never ran
           and THE DRAWER NEVER OPENED — a customer taps the cart icon and
           nothing happens at all. On a flaky mobile connection, with 84% of
           this store's traffic on phones, that is a dead click and a lost cart.

           Catching here lets every caller continue. The drawer opens showing
           the last rendered state instead of refusing to open; the next
           successful refresh corrects it. Deliberately not rethrown — there is
           nothing useful a caller can do about a transport failure, and the
           alternative is the silent no-op above. */
      });
  }

  /* ROUND 64 — cart-open instrumentation.

     Cart -> checkout fell from 69.8% to 42.7% over two weeks. If the drawer
     silently fails to open for some customers, nothing in any dashboard
     distinguishes "nobody tapped the cart" from "the cart didn't open when
     they did" — the two look identical from the outside.

     Deliberately NOT a new analytics vendor. This store already has a
     documented tracking and consent problem, and adding another script to
     measure a conversion bug would be self-defeating. This is a console.warn
     on FAILURE ONLY: Microsoft Clarity already captures console output on real
     sessions, so a failure becomes searchable with zero new requests, zero
     bytes on the wire and nothing to consent-gate — it rides the Clarity
     instance that is already consent-gated from Round 54.

     Success and attempt are intentionally silent. Logging every open would
     bury the signal in noise, and the failure case is the only one nobody can
     currently see.

     HOW TO READ IT: in Clarity, filter sessions by console errors/warnings and
     search for FORGED_CART_OPEN_FAILED. Any hit is a customer who tapped the
     cart and did not get a drawer. Zero hits over a week means the drawer is
     not the leak and the cause is downstream. */
  function verifyOpened() {
    /* Two frames of grace so the rAF that applies is-open has run. Checks the
       thing that actually matters to a customer — is there a visible panel —
       rather than trusting the class alone. */
    setTimeout(function () {
      var visible = !root.hidden &&
                    root.getAttribute('aria-hidden') === 'false' &&
                    root.getBoundingClientRect().width > 0;
      if (!visible && window.console && console.warn) {
        console.warn('FORGED_CART_OPEN_FAILED hidden=' + root.hidden +
                     ' aria=' + root.getAttribute('aria-hidden') +
                     ' width=' + Math.round(root.getBoundingClientRect().width));
      }
    }, 400);
  }

  function open() {
    refresh().then(function () {
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('forged-scroll-lock');
      requestAnimationFrame(function () {
        root.classList.add('is-open');
        var closeBtn = root.querySelector('.forged-cart__close');
        if (closeBtn) closeBtn.focus();
      });
      verifyOpened();
    });
  }

  function close() {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('forged-scroll-lock');
    setTimeout(function () {
      if (!root.classList.contains('is-open')) root.hidden = true;
    }, 320);
  }

  /* ROUND 30 (Emirali: cap quantity at available stock), CORRECTED IN ROUND 37.

     The Round 30 note here claimed "Shopify ALREADY enforces the real limit on
     /cart/add.js and /cart/change.js". That is HALF TRUE, and the wrong half
     mattered. Measured against forged-blackout-jeans S — qty 1, policy DENY,
     tracked true, confirmed via Admin API:

       POST /cart/add.js    quantity 99  -> HTTP 200. Accepted. 99 in the cart.
       POST /cart/change.js quantity 250 -> HTTP 422 "Only 1 item was added to
                                            your cart due to availability."

     So change.js enforces and add.js does NOT. Everything downstream of that
     assumption was over-confident: there is no server-side backstop behind an
     add, which makes the client-side cap the only guard on that path.

     Hence the clamp in addItem below. The 422 surfacing stays — it is still
     the right handling for change.js, and it stays correct when stock moves
     between page load and click, which no client-side number can. */
  function cartErrorEl() {
    return root && root.querySelector('[data-forged-cart-error]');
  }

  function showCartError(text) {
    var el = cartErrorEl();
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    window.clearTimeout(showCartError._t);
    showCartError._t = window.setTimeout(function () { el.hidden = true; }, 6000);
  }

  function clearCartError() {
    var el = cartErrorEl();
    if (el) el.hidden = true;
  }

  /* Shopify 422 body is {message, description}. Prefer description — it's the
     shopper-facing sentence; message is the terser code-ish one. Resolves
     rather than rejects so the refresh still runs and the drawer re-syncs to
     the server's actual state. */
  function handleCartResponse(r) {
    if (r.ok) { clearCartError(); return r; }
    return r.json().then(function (body) {
      showCartError((body && (body.description || body.message)) || 'That quantity isn’t available.');
      return r;
    }, function () {
      showCartError('That quantity isn’t available.');
      return r;
    });
  }

  function changeLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(handleCartResponse).then(refresh);
  }

  /* Round 11 (Emirali: cart-add needs feedback beyond the drawer opening) —
     bump the header cart icon on every successful add. Single choke point
     (window.ForgedCart.addItem) covers PDP, cart-drawer upsells, and any
     future quick-add caller alike. */
  function bumpCartIcon() {
    var icon = document.querySelector('.forged-header__icon--cart');
    if (!icon) return;
    icon.classList.remove('is-bumped');
    void icon.offsetWidth; // restart animation if it's still running from a rapid prior add
    icon.classList.add('is-bumped');
  }

  /* ==================================================================
     ROUND 67 — fly-to-cart.

     A copy of the product image arcs from wherever it was tapped to the
     header cart icon, then the icon bumps and the drawer opens.

     WHY IT LIVES HERE, in addItem's success branch, and not in each caller:
     every add path already funnels through addItem — the PDP button, the
     sticky bar (which forwards to that button), quick-add on collection
     cards, the PDP addon box and the drawer upsell. Hooking the choke point
     wires all five at once and, just as importantly, excludes the automatic
     DONUTS gift for free: forged-gifts.js adds through its own cartFetch and
     never touches addItem, so a gift the customer did not tap cannot produce
     an animation. Verified rather than assumed — see the round report.

     It also inherits Round 39's success gate. addItem already computes `ok`
     precisely because the confirmation must not fire on a failed add; the
     flight is a louder confirmation, so it sits behind the same flag. A 422
     shows the error in the drawer and nothing flies.

     PERFORMANCE — INP is already 260ms and this must not add to it:
       - width/height/left/top are written ONCE at creation. Only transform
         and opacity are animated, both compositor properties, so no frame
         of this animation does layout or paint on the main thread.
       - Web Animations API rather than a rAF loop: the animation is handed
         to the compositor and does not run JS per frame.
       - The clone is removed on finish AND on cancel, so an interrupted
         flight (navigation, a second rapid add) cannot leak nodes.

     pointer-events: none is load-bearing. Dead clicks are at 8.94%; a
     600ms full-screen-crossing element that swallowed taps would directly
     raise that number.
     ================================================================== */
  var FLY_MS = 600;
  var lastTapEl = null;

  /* Capture phase so we still see the element even when a handler stops
     propagation on its way up. Recorded on pointerdown as well as click
     because the drawer's own handlers can re-render between the two. */
  function recordTap(e) { lastTapEl = e.target; }
  document.addEventListener('pointerdown', recordTap, true);
  document.addEventListener('click', recordTap, true);

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Resolve the image the customer actually tapped, most specific first.
     Order matters: a quick-add button lives inside a product card, and a PDP
     addon row lives inside the PDP, so the narrow containers must win. */
  function findFlyImage() {
    var el = lastTapEl;
    if (el && el.closest) {
      var card = el.closest('.frg-pcard');
      if (card) { var ci = card.querySelector('.frg-pcard__media img, img'); if (ci) return ci; }
      var addon = el.closest('.frg-addon');
      if (addon) { var ai = addon.querySelector('img'); if (ai) return ai; }
      var line = el.closest('[data-forged-upsell], .forged-cart__upsell');
      if (line) { var li = line.querySelector('img'); if (li) return li; }
    }
    /* PDP button and sticky bar both mean "the image on screen right now". */
    return document.querySelector('.frg-pdp__main.is-active img');
  }

  function flyToCart() {
    /* Every early return resolves, so addItem's chain continues either way and
       a missing image can never strand the drawer. */
    if (reducedMotion()) return Promise.resolve();
    if (!document.body.animate) return Promise.resolve();      /* no WAAPI */
    if (!root || !root.hidden) return Promise.resolve();       /* drawer already
        open: the cart icon is behind it, so the flight would be invisible.
        The drawer's own in-place confirmation covers this case. */

    var img = findFlyImage();
    var icon = document.querySelector('.forged-header__icon--cart');
    if (!img || !icon) return Promise.resolve();

    var from = img.getBoundingClientRect();
    var to = icon.getBoundingClientRect();
    if (!from.width || !from.height || !to.width) return Promise.resolve();

    /* If the source image is scrolled out of view, the flight would start off
       screen and the clone would fly in from nowhere. That is not an edge case:
       it is the NORMAL case for the sticky add-to-cart bar, which is the main
       mobile purchase path and which the customer taps precisely because the
       gallery is no longer on screen.

       So keep the image for the picture, but start it from the control that was
       actually tapped, which is on screen by definition. Square and modest, so
       a wide button does not launch a distorted banner. */
    if (from.bottom <= 0 || from.top >= window.innerHeight ||
        from.right <= 0 || from.left >= window.innerWidth) {
      var anchor = lastTapEl && lastTapEl.getBoundingClientRect
        ? lastTapEl.getBoundingClientRect() : null;
      if (!anchor || !anchor.width) return Promise.resolve();
      var size = Math.min(120, Math.max(64, anchor.height * 1.6));
      from = {
        left: anchor.left + anchor.width / 2 - size / 2,
        top: anchor.top + anchor.height / 2 - size / 2,
        width: size,
        height: size
      };
    }

    var clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.style.cssText =
      'position:fixed;z-index:600;pointer-events:none;margin:0;padding:0;border-radius:8px;' +
      'object-fit:cover;left:' + from.left + 'px;top:' + from.top + 'px;' +
      'width:' + from.width + 'px;height:' + from.height + 'px;';
    document.body.appendChild(clone);

    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    /* Arc height scales with the distance travelled but is capped, so a card
       near the header does not loop absurdly and a long PDP flight still arcs. */
    var arc = Math.min(170, Math.abs(dy) * 0.35 + 60);

    var anim = clone.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 0.95, offset: 0 },
      { transform: 'translate(' + (dx * 0.5) + 'px,' + (dy * 0.5 - arc) + 'px) scale(0.55)',
        opacity: 0.9, offset: 0.5 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.12)', opacity: 0, offset: 1 }
    ], { duration: FLY_MS, easing: 'cubic-bezier(0.4, 0, 0.6, 1)', fill: 'forwards' });

    /* Cleanup and sequencing are deliberately INDEPENDENT of each other.

       Opening the drawer is functional; the flight is decoration. Measured
       while testing this: under throttling, WAAPI's finish callback can be
       deferred well past the animation's nominal duration — a backgrounded tab
       does exactly that to a real customer. If the drawer waited on that
       callback, a decorative event could hold up the only part that matters.

       So the promise runs on its own timer, and the clone is removed by
       whichever of finish / cancel / guard lands first. Both halves are
       idempotent, so a stranded fixed-position image cannot be left over the
       page — and even if one ever were, it carries pointer-events: none and
       cannot swallow a tap. */
    var removed = false;
    function removeClone() {
      if (removed) return;
      removed = true;
      if (clone.parentNode) clone.parentNode.removeChild(clone);
    }
    anim.onfinish = removeClone;
    anim.oncancel = removeClone;
    setTimeout(removeClone, FLY_MS + 400);

    return new Promise(function (resolve) { setTimeout(resolve, FLY_MS); });
  }

  /* ROUND 39 — the in-drawer add confirmation (see forged-chrome.css for why
     the Round 11 bump alone did not read as one: the drawer opens over the
     header that the bump happens in).

     Auto-hides after 2.4s. That is a HOLD, not the animation — the animation
     itself is the 420ms entrance, inside Emirali's 300-600ms brief. It is
     cleared rather than left standing because the drawer persists across
     subsequent adds, and a confirmation that never leaves stops being tied to
     any particular add.

     Same remove / reflow / add sequence as bumpCartIcon: without the forced
     reflow a second add while the first is still animating re-adds a class the
     element already has, which is a no-op, and the shopper gets no feedback on
     the add they just made. */
  function showAddedConfirm() {
    var el = root && root.querySelector('[data-forged-cart-added]');
    if (!el) return;
    el.hidden = false;
    el.classList.remove('is-in');
    void el.offsetWidth;
    el.classList.add('is-in');

    /* The confirmation sits at the top of .forged-cart__body, which SCROLLS.
       Caught this while verifying: with the body scrolled to the upsells the
       banner rendered at y -477 against a visible region of 69-485, i.e. the
       confirmation existed and was completely off-screen.

       That is not a corner case — it is the drawer's own "Complete the build"
       Add button, which the shopper can only reach BY scrolling down. The one
       add path most likely to need confirming was the one guaranteed not to
       show it.

       Scrolling the body to the top is the fix rather than repositioning the
       banner, because render() also puts the just-added line item at the top:
       one scroll reveals the confirmation and the thing it is confirming.

       Assignment, not scrollTo({behavior:'smooth'}). The first attempt here
       used smooth and it silently did nothing — measured on this element,
       scrollTo smooth left scrollTop at 502 while a plain assignment took it to
       0. Smooth scrolling is driven by the compositor's animation, and that is
       the same machinery that turned out to be suspended for rAF and scroll
       listeners in Round 26 and Round 28.

       Even where smooth does run, it is the wrong trade here: a confirmation
       that depends on an animation completing is a confirmation that can fail
       to be seen, which is the exact bug this block exists to fix. An instant
       reset inside a small panel that just re-rendered reads as part of the
       refresh, not as a jump — and it needs no prefers-reduced-motion guard,
       because an instant reposition is not motion. */
    var body = root.querySelector('.forged-cart__body');
    if (body && body.scrollTop > 0) body.scrollTop = 0;
    window.clearTimeout(showAddedConfirm._t);
    showAddedConfirm._t = window.setTimeout(function () {
      el.classList.remove('is-in');
      el.hidden = true;
    }, 2400);
  }

  function hideAddedConfirm() {
    var el = root && root.querySelector('[data-forged-cart-added]');
    if (!el) return;
    window.clearTimeout(showAddedConfirm._t);
    el.classList.remove('is-in');
    el.hidden = true;
  }

  /* ROUND 37 — the cap moved here, to the choke point, because the PDP is not
     the only way into the cart. Every JS caller routes through addItem: the
     PDP buy button, the PDP addon box, the drawer's Complete-the-build upsell,
     and the homepage quick-add. Capping here means one implementation instead
     of four that drift.

     HOW THE CAP GETS HERE, and its honest limit. /cart.js and /products/x.js
     both expose inventory_management but NEITHER exposes inventory_policy or
     inventory_quantity — verified against the live storefront. So this
     function cannot look stock up; there is no endpoint that returns it for a
     bare variant id. The caller must supply it.

     The PDP can, and does: its Liquid payload carries qty/policy/tracked per
     variant, so it passes `max` (see forged-product.js capFor). Callers that
     genuinely cannot know — the addon box, the upsell, quick-add — pass
     nothing and are unclamped HERE. That is deliberate and safe in practice
     because all three add exactly quantity 1 and only ever offer items whose
     `available` flag is true. The residual gap is the already-in-cart case
     (stock 1, one already in the cart, add one more), which is a known and
     accepted limitation, not something this clamp pretends to solve.

     The three Round 30 gates are preserved by construction: a caller that
     should not be capped simply passes no `max`, so untracked variants,
     continue-selling variants, and any non-positive computed cap all arrive
     here as undefined and pass through untouched. Fail open is the default,
     not a special case — capping those would make 30+ products unbuyable. */
  function addItem(payload) {
    var qty = payload.quantity || 1;
    var max = payload.max;
    if (typeof max === 'number' && isFinite(max) && max > 0 && qty > max) {
      qty = max;
      showCartError('Only ' + max + ' left in stock — we added ' + max + '.');
    }
    /* ROUND 39 — success gating.

       This was a real defect, not a precaution. handleCartResponse resolves
       with the response on BOTH branches (deliberately, so the refresh still
       runs and the drawer re-syncs to the server's actual state), and refresh()
       returns render()'s value, so the response object was gone by the time the
       final .then ran. Result: bumpCartIcon fired on failed adds too.

       Measured on the dev preview before writing this — addItem({id: 1}) gives
       HTTP 422 "Cannot find variant", and the header icon bounced anyway. The
       shopper got a success animation and an error message for the same click.

       Emirali's brief is a confirmation, so the flag is threaded through here
       rather than left implicit. A louder confirmation on an ungated path would
       only have made the wrong signal more convincing.

       The drawer still opens either way — the 422 text lives INSIDE the drawer
       (Round 30), so gating open() would hide the error it exists to show. */
    var ok = false;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: payload.id, quantity: qty })
    })
      .then(handleCartResponse)
      .then(function (r) { ok = !!(r && r.ok); return r; })
      .then(refresh)
      .then(function () {
        /* ROUND 67: the failure branch is unchanged and deliberately opens the
           drawer FIRST — the 422 text lives inside it (Round 30), so it must
           not wait on anything. */
        if (!ok) {
          if (root.hidden) open();
          hideAddedConfirm();
          return;
        }
        /* Success: the image flies, THEN the icon bumps and the drawer opens.
           The drawer is held for the ~600ms flight on purpose — it is
           full-screen on mobile, so opening it first would cover the animation
           completely and there would be nothing to see. The cart COUNT is
           already correct at this point: refresh() ran above, so only the
           bump and the drawer are sequenced behind the flight. */
        return flyToCart().then(function () {
          bumpCartIcon();
          showAddedConfirm();
          if (root.hidden) open();
        });
      });
  }

  function init() {
    root = document.querySelector('[data-forged-cart]');
    if (!root) return;
    refs = {
      count: root.querySelector('[data-forged-cart-count]'),
      empty: root.querySelector('[data-forged-cart-empty]'),
      items: root.querySelector('[data-forged-cart-items]'),
      split: root.querySelector('[data-frg-split]'),
      recover: root.querySelector('[data-frg-recover]'),
      upsells: root.querySelector('[data-forged-cart-upsells]'),
      upsellGrid: root.querySelector('[data-forged-cart-upsell-grid]'),
      footer: root.querySelector('[data-forged-cart-footer]'),
      total: root.querySelector('[data-forged-cart-total]'),
      discountToggle: root.querySelector('[data-forged-cart-discount-toggle]'),
      discountPanel: root.querySelector('[data-forged-cart-discount-panel]'),
      discountInput: root.querySelector('[data-forged-cart-discount-input]'),
      discountApply: root.querySelector('[data-forged-cart-discount-apply]'),
      discountApplied: root.querySelector('[data-forged-cart-discount-applied]'),
      discountLabel: root.querySelector('[data-forged-cart-discount-label]'),
      discountRemove: root.querySelector('[data-forged-cart-discount-remove]'),
      discountMsg: root.querySelector('[data-forged-cart-discount-msg]')
    };

    /* Delegated so the buttons survive every re-render of the drawer. The
       recovery bar lives OUTSIDE the drawer (it is page-level), so this is
       bound on document rather than on root. */
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-frg-split-go]')) { e.preventDefault(); doSplit(); return; }
      if (e.target.closest('[data-frg-recover-go]')) { e.preventDefault(); doRecover(); }
    });

    window.ForgedCart = { open: open, close: close, refresh: refresh, addItem: addItem,
                          markBackordered: markBackordered };

    root.querySelectorAll('[data-forged-cart-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });

    // delegated: any [data-forged-cart-trigger] opens the drawer;
    // qty steppers and upsell Add buttons render dynamically
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-forged-cart-trigger]');
      if (trigger) {
        event.preventDefault();
        open();
        return;
      }
      var qty = event.target.closest('[data-forged-cart-qty]');
      if (qty && root.contains(qty)) {
        var line = parseInt(qty.closest('.forged-cart__item').dataset.line, 10);
        changeLine(line, Math.max(0, parseInt(qty.dataset.forgedCartQty, 10)));
        return;
      }
      var add = event.target.closest('[data-forged-cart-add]');
      if (add && root.contains(add)) {
        addItem({ id: parseInt(add.dataset.forgedCartAdd, 10), quantity: 1 });
      }
    });

    /* ROUND 56 — Clarity reported "cannot read properties of null (reading
       'addEventListener')" on real sessions. Everything below binds to discount
       elements looked up off `root`. `root` is guarded at the top of init, these
       children never were.

       This block is the last thing init does, so a throw here did not cost any
       other handler — but it did surface as an uncaught error, and it left the
       discount UI half-bound (toggle wired, apply not) depending on which lookup
       failed first.

       The markup is not conditional, so on a complete document these always
       exist; the realistic way to get here is a document that never finished
       parsing (aborted or truncated response). 59% of this store's traffic is
       mobile, where that is common. Guarding is the cheap correct fix: the
       discount UI degrades to inert, and the rest of the cart still works. */
    if (!refs.discountToggle || !refs.discountPanel || !refs.discountInput ||
        !refs.discountApply || !refs.discountRemove) return;

    refs.discountToggle.addEventListener('click', function () {
      var isOpen = refs.discountPanel.hidden;
      refs.discountPanel.hidden = !isOpen;
      refs.discountToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen) refs.discountInput.focus();
    });

    /* ---------------------------------------------------------------------
       DEVIATION FROM DONOR (intentional): the ported handler accepted ANY typed
       text and rendered a fake "applied" pill. This now validates against
       Shopify for real: GET /discount/<code> applies the code to the cart
       session, then /cart.js reports it back as {code, applicable}. We only
       show success when applicable === true.

       PLATFORM LIMITATION (verified against this store): Shopify's AJAX API
       does NOT distinguish "code does not exist" from "code exists but its
       conditions aren't met" — both come back applicable:false, and a manual
       redirect probe is opaque in both cases. DONUTS itself is a Buy-X-Get-Y
       code requiring $29+ of specific products, so it is legitimately
       non-applicable in most carts. The failure copy therefore states what is
       actually known rather than falsely asserting "invalid code".
       --------------------------------------------------------------------- */
    function showDiscountMsg(text, kind) {
      if (!refs.discountMsg) return;
      refs.discountMsg.textContent = text || '';
      refs.discountMsg.hidden = !text;
      refs.discountMsg.className = 'forged-cart__discount-msg' + (kind ? ' is-' + kind : '');
    }

    function validateAndApply(code) {
      showDiscountMsg('Checking code…', 'pending');
      refs.discountApply.disabled = true;
      return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { redirect: 'follow' })
        .then(function () { return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var entry = (cart.discount_codes || []).filter(function (d) {
            return String(d.code).toUpperCase() === code.toUpperCase();
          })[0];
          if (entry && entry.applicable) {
            var saved = cart.total_discount ? ' — ' + money(cart.total_discount) + ' off' : '';
            showDiscountMsg('Code applied' + saved, 'ok');
            setDiscount(code);
            refs.discountInput.value = '';
          } else {
            showDiscountMsg(
              'That code isn\u2019t applying to this cart. Check the spelling, or it may have ' +
              'conditions (a minimum spend or specific items) that aren\u2019t met yet.', 'error');
            setDiscount('');            /* never leave a fake pill behind */
          }
          return refresh();
        })
        .catch(function () {
          showDiscountMsg('Could not check that code right now. Please try again.', 'error');
        })
        .then(function () { refs.discountApply.disabled = false; });
    }

    refs.discountApply.addEventListener('click', function () {
      var code = refs.discountInput.value.trim();
      if (!code) return;
      validateAndApply(code);
    });

    refs.discountInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); refs.discountApply.click(); }
    });
    refs.discountInput.addEventListener('input', function () { showDiscountMsg(''); });

    /* ROUND 52 — this button did not remove the discount.

       It called setDiscount(''), which only clears a sessionStorage key and
       re-renders the drawer. The code stayed applied to the Shopify cart, so a
       customer clicked the x, watched the pill disappear, and was still carrying
       the discount at checkout. The display was the only thing that changed.

       POST /cart/update.js {discount:''} is what actually clears it — verified
       against /cart.js, which is the only trustworthy signal here given the
       drawer's own display is exactly what was lying.

       Structured like validateAndApply above: act, then re-read the real cart
       and only report success if the cart agrees. On failure the pill is left
       standing, because a code that is still applied must not be shown as
       removed — that is the same false-confidence bug in the other direction.

       refresh() at the end re-renders from real cart state, which also drives
       forged-gifts.js via onCartRendered — so removing DONUTS reconciles the
       auto-added Grip Donuts back out. */
    refs.discountRemove.addEventListener('click', function () {
      showDiscountMsg('Removing…', 'pending');
      refs.discountRemove.disabled = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      })
        .then(function () { return fetch('/cart.js'); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var stillOn = (cart.discount_codes || []).length > 0;
          if (stillOn) {
            showDiscountMsg('That code could not be removed. Please try again.', 'error');
          } else {
            setDiscount('');
            refs.discountInput.value = '';
            showDiscountMsg('');
          }
        })
        .catch(function () {
          showDiscountMsg('That code could not be removed. Please try again.', 'error');
        })
        .then(function () {
          refs.discountRemove.disabled = false;
          return refresh();
        });
    });
  }

  /* The recovery bar must appear on a plain page view — the customer coming
     back from Shopify's thank-you page has not opened the cart. Reads the cart
     once on load purely to check the saved variants are not already in it. */
  function bootRecoveryBar() {
    if (!readSaved()) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { renderRecover(c); })
      .catch(function () { renderRecover({ items: [] }); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); bootRecoveryBar(); });
  } else {
    init();
    bootRecoveryBar();
  }
})();
